import './utils/process-polyfill';

import React from "react";
import ReactDOM from "react-dom/client";
import { Provider as StoreProvider } from "react-redux";
import { HelmetProvider } from "react-helmet-async";

import { store, persistor } from "./redux/store";
import { PersistGate } from "redux-persist/integration/react";

import * as serviceWorker from './serviceWorker';

import App from "./App";
const WildcardApp = React.lazy(() => import("./WildcardApp"));

import reportWebVitals from "./reportWebVitals";
import { loadGtagIfConsented } from "./utils/loadGtagWhenAllowed";

import './index.css';
import './tailwind-output.css';

// ============================================================================
// SUBDOMAIN ROUTING INTERCEPTOR
// ============================================================================
// Determine if we are on a custom company subdomain (e.g. acme.keelzo.com)
// If so, we bypass the normal app routing and load the WildcardApp directly.
let isSubdomain = false;
let subdomainSlug = "";
const hostname = window.location.hostname;

// Filter out root domains, common dev hosts, and Azure Front Door endpoints
const rootDomains = ["keelzo.com", "app.keelzo.com", "www.keelzo.com", "dev.keelzo.com", "localhost", "127.0.0.1"];
const isRootDomain = rootDomains.includes(hostname)
  || hostname.endsWith(".azurefd.net")        // Azure Front Door endpoints
  || hostname.endsWith(".azurestaticapps.net"); // Azure Static Web Apps
if (!isRootDomain) {
  // It is a subdomain! Extract the first part
  // e.g. "acme.keelzo.com" -> "acme"
  // e.g. "acme.localhost" -> "acme"
  subdomainSlug = hostname.split(".")[0];
  if (subdomainSlug && subdomainSlug !== "app" && subdomainSlug !== "www" && subdomainSlug !== "dev") {
    isSubdomain = true;
  }
}

// ============================================================================
// PRODUCTION CONSOLE SUPPRESSION (FOOLPROOF SAFETY NET)
// Ensures absolutely zero logs escape into the production browser console,
// including warnings from third-party libraries or unhandled errors.
// ============================================================================
if (process.env.NODE_ENV === 'production') {
  console.log = () => { };
  console.info = () => { };
  console.warn = () => { };
  console.error = () => { };
  console.debug = () => { };
}
// ============================================================================
// Suppress react-error-overlay (DEV ONLY)
// The CRA error overlay shows a red band with file/route URLs at the top
// whenever a transient runtime error occurs during navigation or refresh.
// Production builds do not show this overlay; run only in dev to avoid TBT.
// ============================================================================
if (process.env.NODE_ENV === 'development') {
  (function suppressErrorOverlay() {
  const style = document.createElement('style');
  style.innerHTML = `
    iframe#webpack-dev-server-client-overlay { display: none !important; }
    body > iframe[style*="position: fixed"][style*="z-index"] { display: none !important; }
    /* react-error-overlay 6.x renders a div for runtime errors */
    body > div[style*="position: fixed"][style*="inset: 0"],
    body > div[style*="position:fixed"][style*="top: 0"][style*="left: 0"][style*="right: 0"],
    [class*="react-error-overlay"],
    [class*="error-overlay"] { display: none !important; }
  `;
  document.head.appendChild(style);

  const stopOverlay = () => {
    const iframes = document.querySelectorAll('iframe');
    iframes.forEach((iframe) => {
      const src = iframe.getAttribute('src') || '';
      const s = iframe.getAttribute('style') || '';
      if (
        src === '' &&
        (s.includes('position: fixed') || s.includes('z-index'))
      ) {
        iframe.remove();
      }
    });
    const divs = document.querySelectorAll('body > div');
    divs.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const div = node;
      if (div.classList?.contains('session-expire-overlay')) return;
      const s = div.getAttribute('style') || '';
      const isFixedFullScreen =
        (s.includes('position: fixed') || s.includes('position:fixed')) &&
        (s.includes('inset: 0') || (s.includes('top: 0') && s.includes('left: 0'))) &&
        (s.includes('z-index:') || s.includes('zIndex'));
      const isFixedTop =
        (s.includes('position: fixed') || s.includes('position:fixed')) &&
        (s.includes('top: 0') || s.includes('top:0'));
      const hasErrorContent = div.querySelector('pre, [class*="error-overlay"]') ||
        (div.textContent && /\/dashboard\/inbox\/records\/[a-f0-9]{24}\/[a-f0-9]{24}/.test(div.textContent));
      if ((isFixedFullScreen || isFixedTop) && hasErrorContent) {
        div.style.display = 'none';
      }
    });
  };

  const overlayObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (node instanceof HTMLIFrameElement) {
          const s = node.getAttribute('style') || '';
          if (s.includes('position: fixed') || s.includes('z-index')) {
            node.style.display = 'none';
          }
        }
        if (node instanceof HTMLElement && node.tagName === 'DIV') {
          if (node.classList?.contains('session-expire-overlay')) return;
          const s = node.getAttribute('style') || '';
          const isOverlay =
            (s.includes('position: fixed') || s.includes('position:fixed')) &&
            (s.includes('inset: 0') || s.includes('top: 0') || s.includes('top:0')) &&
            (s.includes('z-index:') || s.includes('zIndex'));
          const hasErrorContent = node.querySelector('pre, [class*="error-overlay"]') ||
            (node.textContent && /\/dashboard\/inbox\/records\/[a-f0-9]{24}\/[a-f0-9]{24}/.test(node.textContent));
          if (isOverlay && hasErrorContent) {
            node.style.display = 'none';
          }
        }
      }
    }
  });
  const init = () => {
    if (document.body) {
      overlayObserver.observe(document.body, { childList: true, subtree: true });
      stopOverlay();
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('load', stopOverlay);
  })();
}

// ============================================================================
// Console noise suppression (DEV ONLY)
// - Removes verbose debug logs (🔍 ✅ 📬 📊 etc.) without affecting errors.
// - Keeps console.error intact.
// ============================================================================
if (process.env.NODE_ENV === 'development') {
  const shouldSuppress = (firstArg: unknown) =>
    typeof firstArg === 'string' &&
    /^(🔍|✅|📬|📊|📤|🔄|🟢|🔵|ℹ️|\[TopBar\]|\[DocumentsSection\]|\[SocialConnections\]|\[Skills\]|\[Services\]|\[Projects\]|\[Languages\]|\[Highlights\]|\[Experience\]|\[Education\]|\[Certificates\]|\[Basicinfo\]|\[AboutMe\]|\[ResumeUpload\]|Socket )/.test(firstArg);

  const originalLog = console.log.bind(console);
  const originalWarn = console.warn.bind(console);

  console.log = (...args: any[]) => {
    if (shouldSuppress(args[0])) return;
    originalLog(...args);
  };

  console.warn = (...args: any[]) => {
    if (shouldSuppress(args[0])) return;
    originalWarn(...args);
  };
}

// Ensure process is available for any late-loading scripts
// This runs after DOM is ready to catch dynamically loaded scripts
if (typeof document !== 'undefined') {
  const ensureProcess = () => {
    if (typeof process === 'undefined') {
      const processPolyfill = {
        env: {},
        browser: true,
        version: '16.0.0',
        platform: 'browser',
        nextTick: function (fn: any) { (setImmediate || setTimeout)(fn, 0); },
        cwd: function () { return '/'; },
        exit: function () { },
        argv: [],
        versions: { node: '16.0.0' },
        pid: 1,
        title: 'browser',
        umask: function () { return 0; }
      };

      const setP = (obj: any) => {
        try {
          if (obj) {
            obj.process = processPolyfill;
            try {
              Object.defineProperty(obj, 'process', {
                value: processPolyfill,
                writable: true,
                configurable: true,
                enumerable: false
              });
            } catch (e) { }
          }
        } catch (e) { }
      };

      if (typeof window !== 'undefined') setP(window);
      if (typeof globalThis !== 'undefined') setP(globalThis);
      if (typeof global !== 'undefined') setP(global);
      if (typeof self !== 'undefined') setP(self);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureProcess);
  } else {
    ensureProcess();
  }

  // Also set up a mutation observer to catch scripts loaded after DOM ready
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(() => {
      ensureProcess();
    });
    observer.observe(document.head, { childList: true, subtree: true });
  }
}

// expose store when run in Cypress
// if (window.Cypress) {
//   window.Storage = store;
// }

loadGtagIfConsented();

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <HelmetProvider>
    <StoreProvider store={store}>
      <PersistGate loading={
        <div className="global-spinner-container">
          <div className="global-spinner"></div>
        </div>
      } persistor={persistor}>
        <React.Suspense fallback={
          <div className="global-spinner-container">
            <div className="global-spinner"></div>
          </div>
        }>
          {isSubdomain ? (
            <WildcardApp slug={subdomainSlug} />
          ) : (
            <App />
          )}
        </React.Suspense>
      </PersistGate>
    </StoreProvider>
  </HelmetProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// serviceWorker.register();